import { useEffect } from 'react';
import { SITE_URL, SITE_NAME } from '@/lib/site';

export interface SeoInput {
  title: string;
  description?: string;
  keywords?: string;
  path: string;
  type?: 'website' | 'article';
  image?: string;
  noindex?: boolean;
}

const FALLBACK_DESCRIPTION =
  'Explore the AllChinaBuy spreadsheet with curated product finds, trusted seller info, QC guides, shipping resources and buying tips for smarter product research.';
const FALLBACK_KEYWORDS =
  'AllChinaBuy spreadsheet, AllChinaBuy product spreadsheet, AllChinaBuy trusted sellers, AllChinaBuy buying guide, AllChinaBuy product search';
const FALLBACK_IMAGE =
  'https://readdy.ai/api/search-image?query=Wide%20artistic%20flat%20lay%20arrangement%20of%20modern%20streetwear%20fashion%20products%20including%20white%20leather%20sneakers%20a%20folded%20sage%20green%20hoodie%20baseball%20caps%20and%20a%20canvas%20tote%20bag%20neatly%20arranged%20on%20a%20soft%20warm%20cream%20studio%20background%20with%20gentle%20natural%20shadows%20clean%20minimalist%20editorial%20e-commerce%20aesthetic%20soft%20diffused%20window%20lighting%20warm%20beige%20and%20sage%20green%20tones%20high%20detail%20balanced%20harmonious%20composition&width=1600&height=900&seq=hero-2026-x1&orientation=landscape';

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function useSeo(input: SeoInput) {
  useEffect(() => {
    const title = input.title;
    const description = input.description || FALLBACK_DESCRIPTION;
    const keywords = input.keywords || FALLBACK_KEYWORDS;
    const path = input.path === '/' ? '/' : input.path;
    const url = `${SITE_URL}${path}`;
    const type = input.type || 'website';
    const image = input.image || FALLBACK_IMAGE;

    document.title = title;

    upsertMeta('name', 'description', description);
    upsertMeta('name', 'keywords', keywords);
    upsertMeta('name', 'robots', input.noindex ? 'noindex, follow' : 'index, follow');
    upsertCanonical(url);

    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', image);

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', image);
  }, [input.title, input.description, input.keywords, input.path, input.type, input.image, input.noindex]);
}